import { decodeSessionClaims } from '@/lib/session-claims'

/**
 * Where a request goes when a client is signed in (Client portal, read-only
 * workflows). The portal renders the staff pages themselves, so their data
 * hooks keep asking for `/analytics/...` and `/clients/:id/...`. Those are
 * staff routes the gateway refuses to a client token; the same reads exist
 * under `/portal/me/stores/:id/...`, checked per contact and per page, and a
 * client's request is sent there instead.
 *
 * Anything else staff-only is refused here rather than sent: a 401 from the
 * gateway would spend the single-use refresh cookie on a call that can never
 * succeed.
 */

const STAFF_ONLY = /^\/(agencies|analytics|clients|oauth|stores|users)(\/|\?|$)/

export function portalPathFor(
  path: string,
  req: { method: string; headers: Record<string, string> },
): string | null {
  if (path.startsWith('/portal/')) return path
  if (req.method !== 'GET') return STAFF_ONLY.test(path) ? null : path

  const analytics = /^\/analytics\/(.+)$/.exec(path)
  if (analytics) {
    const storeId = req.headers['X-Store-Id'] ?? req.headers['x-store-id']
    return storeId ? `/portal/me/stores/${encodeURIComponent(storeId)}/analytics/${analytics[1]}` : null
  }

  const store = /^\/clients\/([^/?]+)\/(calendar\/events|scorecards\/(?:weekly|monthly))(\?.*)?$/.exec(path)
  if (store) return `/portal/me/stores/${store[1]}/${store[2]}${store[3] ?? ''}`

  return STAFF_ONLY.test(path) ? null : path
}

/** The `routeRequest` hook for `configureApi`: staff paths pass untouched. */
export function routeRequestFor(getToken: () => string | null) {
  return (path: string, req: { method: string; headers: Record<string, string> }): string | null => {
    const token = getToken()
    if (!token || decodeSessionClaims(token)?.audience !== 'client') return path
    return portalPathFor(path, req)
  }
}
